import { useEffect, useState } from 'react';
import { FiCheck, FiCopy, FiRefreshCw } from 'react-icons/fi';

import { UserAvatar } from 'components/user-avatar';
import { useAuth } from 'context/AuthContext';
import { FOCO_VISIVEL } from 'utils/foco';

import AvatarDoJJ from './AvatarDoJJ';
import { MensagemDoChat } from './useConversaComJJ';

const ACAO = `flex h-8 w-8 cursor-pointer items-center justify-center rounded-jj-full border-0 bg-transparent text-brand-blue hover:bg-surface-200 aria-disabled:cursor-not-allowed aria-disabled:text-content-soft aria-disabled:hover:bg-transparent ${FOCO_VISIVEL}`;

/** Tempo do ícone de "Resposta copiada" antes de voltar a "Copiar resposta". */
const TEMPO_DA_COPIA = 2000;

interface MessageChatIAProps {
  mensagem: MensagemDoChat;
  /** Com uma resposta pendente, "Pedir outra resposta" fica desabilitado (o pedido seria ignorado). */
  respondendo: boolean;
  onPedirOutra: () => void;
  /** Avisa a janela, que anuncia "Resposta copiada" no `role="status"`. */
  onCopiada: () => void;
}

/** Balão de uma mensagem: do JJ (branco, à esquerda) ou do funci (azul, à direita). */
export default function MessageChatIA({ mensagem, respondendo, onPedirOutra, onCopiada }: MessageChatIAProps) {
  const { user } = useAuth();
  const [copiada, setCopiada] = useState(false);
  const doJJ = mensagem.role === 'assistant';

  useEffect(() => {
    if (!copiada) return;
    const temporizador = setTimeout(() => setCopiada(false), TEMPO_DA_COPIA);
    return () => clearTimeout(temporizador);
  }, [copiada]);

  async function copiar() {
    try {
      await navigator.clipboard.writeText(mensagem.content);
      setCopiada(true);
      onCopiada();
    } catch (erro) {
      console.error(erro);
    }
  }

  if (!doJJ) {
    return (
      <li className="flex items-end justify-end gap-2">
        <p className="m-0 max-w-[80%] whitespace-pre-line rounded-jj-xl rounded-br-jj-xs bg-brand-blue px-4 py-3 text-sm leading-normal text-brand-white">
          <span className="sr-only">Você: </span>
          {mensagem.content}
        </p>
        <span aria-hidden="true" className="h-10 w-10 shrink-0 overflow-hidden rounded-jj-full bg-surface-200">
          <UserAvatar data={{ ...user, ...user?.extra }} responsive />
        </span>
      </li>
    );
  }

  return (
    <li className="flex flex-col gap-1">
      <div className="flex items-end gap-2">
        <AvatarDoJJ tamanho="pequeno" />
        <p className="m-0 max-w-[80%] whitespace-pre-line rounded-jj-xl rounded-bl-jj-xs bg-brand-white px-4 py-3 text-sm leading-normal text-content shadow-jj-01">
          <span className="sr-only">Assistente JJ: </span>
          {mensagem.content}
        </p>
      </div>
      <div className="ml-12 flex gap-1">
        <button type="button" className={ACAO} onClick={copiar} aria-label={copiada ? 'Resposta copiada' : 'Copiar resposta'}>
          {copiada ? <FiCheck aria-hidden="true" size={16} /> : <FiCopy aria-hidden="true" size={16} />}
        </button>
        {/* CHAT-02: a abertura do JJ não tem "Pedir outra resposta". `aria-disabled`, e não `disabled`: o botão
            continua no Tab, e o foco não se perde quando ele desabilita no meio do clique. */}
        {!mensagem.abertura && (
          <button
            type="button"
            className={ACAO}
            onClick={respondendo ? undefined : onPedirOutra}
            aria-label="Pedir outra resposta"
            aria-disabled={respondendo || undefined}
          >
            <FiRefreshCw aria-hidden="true" size={16} />
          </button>
        )}
      </div>
    </li>
  );
}
